import { z } from "zod";
import {
  CURRENCIES,
  LINE_ITEM_TYPES,
  OFFER_STATUSES,
  type OfferStatus,
} from "../status.js";

const decimalInput = z
  .union([z.number().finite(), z.string().regex(/^-?\d{1,12}(\.\d{1,3})?$/)])
  .transform(String);
const moneyInput = z
  .union([z.number().finite(), z.string().regex(/^-?\d{1,12}(\.\d{1,2})?$/)])
  .transform(String);

export const lineItemInputSchema = z.object({
  type: z.enum(LINE_ITEM_TYPES).default("work"),
  title: z.string().trim().min(1).max(180),
  description: z.string().trim().max(4000).optional(),
  quantity: decimalInput.default("1"),
  unit: z.string().trim().min(1).max(20).default("pcs"),
  unitPrice: moneyInput.default("0"),
  discountPercent: decimalInput.nullable().optional(),
  discountAmount: moneyInput.nullable().optional(),
  taxRate: decimalInput.default("0"),
  sortOrder: z.number().int().min(0).optional(),
  catalogItemId: z.string().uuid().nullable().optional(),
  /** Invoices only: the accepted offer line this line bills. */
  sourceOfferItemId: z.string().uuid().nullable().optional(),
});
export type LineItemInput = z.infer<typeof lineItemInputSchema>;

const optionalLongText = z.string().trim().max(4000).nullable().optional();

/** Document-level discount inputs; both may be combined (percent first). */
export const documentDiscountSchema = z.object({
  discountPercent: z
    .union([z.number().finite(), z.string().regex(/^\d{1,3}(\.\d{1,3})?$/)])
    .transform(String)
    .refine((v) => Number(v) <= 100, "Discount cannot exceed 100%")
    .nullable()
    .optional(),
  discountAmount: z
    .union([z.number().finite(), z.string().regex(/^\d{1,12}(\.\d{1,2})?$/)])
    .transform(String)
    .nullable()
    .optional(),
});

export const offerCreateSchema = z
  .object({
    customerId: z.string().uuid(),
    requestId: z.string().uuid().nullable().optional(),
    projectId: z.string().uuid().nullable().optional(),
    title: z.string().trim().min(1).max(180),
    introText: optionalLongText,
    termsText: optionalLongText,
    internalNotes: optionalLongText,
    customerNotes: optionalLongText,
    currency: z.enum(CURRENCIES).optional(),
    validUntil: z.string().date().nullable().optional(),
    items: z.array(lineItemInputSchema).max(500).default([]),
  })
  .merge(documentDiscountSchema);
export type OfferCreateDto = z.infer<typeof offerCreateSchema>;

export const offerUpdateSchema = offerCreateSchema
  .omit({ customerId: true, requestId: true, projectId: true })
  .partial()
  .extend({ version: z.number().int().min(1) });
export type OfferUpdateDto = z.infer<typeof offerUpdateSchema>;

export const offerActionSchema = z.object({
  version: z.number().int().min(1),
  note: z.string().trim().max(1000).optional(),
});
export type OfferActionDto = z.infer<typeof offerActionSchema>;

export const documentSendSchema = z.object({
  version: z.number().int().min(1),
  /** Recipient override; defaults to the customer's email address. */
  email: z.string().trim().toLowerCase().email().optional(),
  /** false = mark as sent without emailing (handed over or posted). */
  deliver: z.boolean().default(true),
});
export type DocumentSendDto = z.infer<typeof documentSendSchema>;

export const offerListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  q: z.string().trim().max(120).optional(),
  status: z.enum(OFFER_STATUSES).optional(),
  customerId: z.string().uuid().optional(),
  requestId: z.string().uuid().optional(),
  projectId: z.string().uuid().optional(),
  /** Hide superseded revisions (default). */
  includeSuperseded: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional(),
  sortField: z
    .enum([
      "number",
      "title",
      "validUntil",
      "grandTotal",
      "status",
      "createdAt",
    ])
    .default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc"),
});
export type OfferListQuery = z.infer<typeof offerListQuerySchema>;

export interface CommercialLineResponse {
  id: string;
  position: number;
  type: string;
  title: string;
  description?: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  discountPercent?: string;
  discountAmount?: string;
  taxRate: string;
  lineTotal: string;
  sortOrder: number;
  catalogItemId?: string;
  sourceOfferItemId?: string;
}

export interface CommercialTotalsResponse {
  discountPercent?: string;
  discountAmount?: string;
  subtotal: string;
  documentDiscount: string;
  netExclTax: string;
  taxBreakdown: Array<{ rate: string; net: string; tax: string }>;
  taxTotal: string;
  totalInclTax: string;
  roundingAdjustment: string;
  grandTotal: string;
}

export interface DocumentRef {
  id: string;
  number: string;
  status: string;
}

export interface CustomerRef {
  id: string;
  number: string;
  displayName: string;
  email?: string;
}

export interface OfferResponse extends CommercialTotalsResponse {
  id: string;
  number: string;
  /** Number including the revision suffix, e.g. "OFF-2026-000001-R2". */
  displayNumber: string;
  customer: CustomerRef;
  request?: DocumentRef;
  project?: { id: string; number: string; title: string };
  previousRevisionId?: string;
  nextRevisionId?: string;
  invoices: DocumentRef[];
  title: string;
  introText?: string;
  termsText?: string;
  internalNotes?: string;
  customerNotes?: string;
  currency: string;
  status: OfferStatus;
  revision: number;
  validUntil?: string;
  items: CommercialLineResponse[];
  sentAt?: string;
  decidedAt?: string;
  decisionNote?: string;
  pdfAvailable: boolean;
  /** Accepted offers: quantity per offer line not yet invoiced. */
  remainingToInvoice?: Array<{ offerItemId: string; quantity: string }>;
  /** Latest email delivery state for this document. */
  delivery?: {
    status: "pending" | "processing" | "done" | "failed";
    error?: string;
  };
  createdBy?: { id: string; name: string };
  version: number;
  createdAt: string;
  updatedAt: string;
}
